import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import * as styles from './DraggableList.module.css';

/**
 * DraggableList - A list of draggable items
 * 
 * @param {Object} props
 * @param {string} props.label - Label for the list
 * @param {Array} props.items - Items to display
 * @param {Function} props.renderItem - Custom render function for items
 * @param {Function} props.onDragStart - Callback when drag starts
 * @param {Function} props.onDragEnd - Callback when drag ends
 * @param {string} props.emptyMessage - Message to show when list is empty
 * @param {string} props.helpText - Help text shown below the list
 * @param {string} props.className - Additional CSS class
 */
function DraggableList({
  label,
  items,
  renderItem,
  onDragStart,
  onDragEnd,
  emptyMessage,
  helpText,
  className,
}) {
  const handleDragStart = useCallback((e, item) => {
    e.dataTransfer.setData('application/json', JSON.stringify(item));
    e.dataTransfer.effectAllowed = 'move';
    if (onDragStart) {
      onDragStart(e, item);
    }
  }, [onDragStart]);

  const handleDragEnd = useCallback((e) => {
    if (onDragEnd) {
      onDragEnd(e);
    }
  }, [onDragEnd]);

  const containerClasses = [styles.container, className].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {label && <div className={styles.label}>{label}</div>}
      
      {helpText && <div className={styles.helpText}>{helpText}</div>}

      <div className={styles.itemList}>
        {items && items.length > 0 ? (
          items.map((item, index) => (
            <div
              key={typeof item === 'string' ? item : item?.value || item?.name || index}
              className={styles.item}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragEnd={handleDragEnd}
            >
              {renderItem ? renderItem(item) : (
                <>
                  <div className={styles.dragHandle}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4h1v1H6V4zm3 0h1v1H9V4zM6 7h1v1H6V7zm3 0h1v1H9V7zM6 10h1v1H6v-1zm3 0h1v1H9v-1z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className={styles.itemContent}>
                    <span className={styles.itemName}>
                      {typeof item === 'string' ? item : item?.label || item?.name || item?.value || 'Unknown'}
                    </span>
                    {item?.type && <span className={styles.itemType}>{item.type}</span>}
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <div className={styles.emptyMessage}>
            {emptyMessage || 'No items available'}
          </div>
        )}
      </div>
    </div>
  );
}

DraggableList.propTypes = {
  label: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.any), // eslint-disable-line react/forbid-prop-types
  renderItem: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  emptyMessage: PropTypes.string,
  helpText: PropTypes.string,
  className: PropTypes.string,
};

DraggableList.defaultProps = {
  label: '',
  items: [],
  renderItem: null,
  onDragStart: null,
  onDragEnd: null,
  emptyMessage: '',
  helpText: '',
  className: '',
};

export default DraggableList;

